import { invalidateAbsences } from '@/api/absence';
import { ApiError, api } from '@/api/client';
import { invalidateShifts } from '@/api/shifts';
import { getToken } from '@/auth/store';
import { API_BASE } from '@/config';
import { T } from '@/i18n';
/**
 * The chat panel.
 *
 * assistant-ui holds the thread, the composer, the in-flight state and the error state; the
 * AI SDK transport carries the turn to POST /poc/chat and streams the answer back. Nothing
 * here parses what the manager typed, and nothing here decides what they may see: the token
 * goes with the request, and the server answers for that person and their company.
 */
import { useTenant } from '@/shell/tenant';
import { HuginMark } from '@/ui/icons';
import {
  AssistantRuntimeProvider,
  ComposerPrimitive,
  ErrorPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useAuiState,
  useMessagePartText,
} from '@assistant-ui/react';
import { AssistantChatTransport, useChatRuntime } from '@assistant-ui/react-ai-sdk';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useInRouterContext } from 'react-router-dom';
import { create } from 'zustand';
import {
  type ActionCard,
  type BriefingItem,
  type ReportItem,
  type StoredMessage,
  cancelAction,
  clearThread,
  confirmAction,
  editAction,
  loadBriefing,
  loadPending,
  loadReport,
  loadThread,
  sendReport,
} from './actions';
import {
  type Attached,
  type ProposalAsk,
  SHOW_PROPOSALS,
  editsIn,
  proposalIn,
  saveOfferIn,
  takeSend,
  useAttached,
  useDock,
  useProposalAsk,
} from './attach';
import './panel.css';
import { trapTab } from '@/ui/page';
import { KeptList, SettingForm, canEdit, noteOutcome, valuesOf } from './SettingEdit';
import { ReportCards } from './reports';

/**
 * A question Hugin asks itself when the panel next opens (plan 0030, owner 2026-10-05): a cold email's
 * «Se selv» opens a demo whose next week is empty, and the visitor watches Hugin build it. Kept in
 * sessionStorage so it survives the navigation to Vaktplan and is used exactly once.
 */
const ASK_KEY = 'timefly.hugin.ask';
export function askOnOpen(text: string) {
  try {
    sessionStorage.setItem(ASK_KEY, text);
  } catch {
    // no storage: the demo still opens, with Hugin's starters to press
  }
}
/**
 * Where Hugin's opening question is (set below; the welcome, shell/Welcome.tsx, reads it, #130): pending until the panel has read the
 * thread, showing while the opening panel or its phone bubble is up, done once it is put away or there
 * was nothing to open.
 */
export const useHuginOpening = create<{ state: 'pending' | 'showing' | 'done' }>(() => ({
  state: 'pending',
}));
export const setHuginOpening = (state: 'pending' | 'showing' | 'done') => useHuginOpening.setState({ state });

/**
 * A request typed into Hugin's box and left for the person to send (the welcome overview, #130): the
 * panel opens with the words in place and nothing is asked until Send, so a press costs no question.
 */
let draft: string | null = null;
export function draftForHugin(text: string) {
  draft = text;
  window.dispatchEvent(new Event('timefly:ai-open'));
  window.dispatchEvent(new Event('timefly:ai-draft'));
}
const askWaiting = () => {
  try {
    return sessionStorage.getItem(ASK_KEY);
  } catch {
    return null;
  }
};
function takeAsk() {
  const text = askWaiting();
  try {
    sessionStorage.removeItem(ASK_KEY);
  } catch {
    // nothing to clear
  }
  return text;
}

/**
 * A date a person would say, from a YYYY-MM-DD.
 *
 * The cards used to print the ISO date the API speaks, so a manager read "2026-09-22" where the
 * rest of the product writes a Norwegian date. Midday UTC, so the calendar day cannot slip either
 * side of midnight in Oslo. The year is left off when it is this one, which is how people write it.
 */
function dag(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString('nb-NO', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(d.getUTCFullYear() === new Date().getFullYear() ? {} : { year: 'numeric' }),
  });
}

/**
 * Inline **bold**, *italic* and `code` inside one line.
 *
 * ponytail: a bounded reader, not a markdown parser. It covers what the model actually writes in
 * this chat. Links, images, tables, block quotes and nested emphasis are left as written, which is
 * the same as today's behaviour rather than a regression. Everything is built as React nodes, so
 * nothing the model produces can become markup.
 */
function inline(text: string, key: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|(?<![*\w])\*([^*\n]+)\*(?!\*)|`([^`]+)`/g;
  let at = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > at) out.push(text.slice(at, m.index));
    if (m[1] !== undefined) out.push(<strong key={`${key}-${m.index}`}>{m[1]}</strong>);
    else if (m[2] !== undefined) out.push(<em key={`${key}-${m.index}`}>{m[2]}</em>);
    else out.push(<code key={`${key}-${m.index}`}>{m[3]}</code>);
    at = m.index + m[0].length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}

/**
 * The model writes markdown, because that is what models do. The panel used to print it: asterisks
 * on screen, and a list collapsed into one line with hyphens in the middle of a sentence. Telling
 * a model to stop writing lists works about as well as you would expect, so the panel reads them.
 */
function rich(text: string): React.ReactNode {
  const lines = text.split('\n');
  const out: React.ReactNode[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (!bullets.length) return;
    const items = bullets;
    bullets = [];
    out.push(
      <ul className="pc-list" key={`l${out.length}`}>
        {items.map((b, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: a rendered list that is never reordered or edited; the position is the identity
          <li key={i}>{inline(b, `${out.length}-${i}`)}</li>
        ))}
      </ul>,
    );
  };
  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      bullets.push(((bullet ?? numbered)?.[1] ?? '').trim());
      continue;
    }
    flush();
    if (!line.trim()) continue;
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    out.push(
      <p key={`p${out.length}`}>
        {heading ? (
          <strong>{inline(heading[1] ?? '', `h${out.length}`)}</strong>
        ) : (
          inline(line, `p${out.length}`)
        )}
      </p>,
    );
  }
  flush();
  return out;
}

/**
 * One text part of a message, read rather than printed.
 *
 * The server's own lines are caught here rather than at the message, because the runtime merges
 * consecutive assistant messages: an outcome written after a decision lands in the same message as
 * the proposal that preceded it. On screen that read as one block saying a change was waiting and
 * done at once. As a part it keeps its own shape whatever the runtime does with the message.
 */
/**
 * What the server's line says happened, from the fixed words it opens with (chatController.js record,
 * ops.js outcomeLine): a cancel changed nothing, a refusal or an unknown outcome is a warning, the rest
 * is done. Read from the text, so lines already stored in a conversation get it too (owner, 2026-10-01:
 * a cancel and a refusal were drawn green with a ✓ as if done).
 */
export function toneOf(text: string): 'ok' | 'neutral' | 'warn' | 'failed' {
  // the turn itself failed (the engine down, a missing key): an error, never a ✓ (beta 2026-11-05)
  if (FAILED.test(text)) return 'failed';
  if (/^Forslaget ble avbrutt/.test(text)) return 'neutral';
  if (
    /Ikke utført|Delvis utført|Vet ikke om|Ukjent utfall|Utfallet .*ukjent|Ikke forsøkt|Ikke registrert|Endret siden forslaget|mislyktes|ikke bekreftet|utfall er ukjent/i.test(
      text,
    )
  )
    return 'warn';
  return 'ok';
}

/** The server's lines for a turn that got no answer (chatController.js answered, COULD_NOT_ANSWER). */
const FAILED = /^(Svaret stoppet før det kom noe tilbake|AI-assistenten kunne ikke svare)/;

function Text() {
  const { text } = useMessagePartText();
  const { systemLines } = React.useContext(Pending);
  const line = String(text || '').trim();
  // the failure line streamed into a turn is the server's too, though not yet marked as such
  if (systemLines.has(line) || FAILED.test(line)) {
    const tone = toneOf(line);
    return <div className={tone === 'ok' ? 'pc-system' : `pc-system pc-${tone}`}>{rich(text)}</div>;
  }
  return <div className="pc-text">{rich(text)}</div>;
}

/** The assistant's name. One string: the panel, the turns and the tab all read it from here. */
// Hugin (owner, 2026-09-27): Odin's raven, who flies out over the world each morning and tells
// what he saw; the assistant looks over the schedule and tells the manager what needs them. The
// name is always shown with "AI", so nobody takes it for a colleague.
const ASSISTANT = 'Hugin';
// a phone or tablet: no keyboard until the person taps the box
const TOUCH = typeof window !== 'undefined' && Boolean(window.matchMedia?.('(pointer: coarse)').matches);

/**
 * The cards waiting for a decision, and the turn each one was proposed in.
 *
 * The card used to float outside the conversation, so the proposal and the sentence that made it
 * were in two different places. It belongs where it was made. A proposal carries its action_id in
 * the tool result that created it, and that result is part of the assistant message, so the message
 * can find its own cards. Anything that cannot be matched to a message, because the turn that made
 * it is no longer in the transcript, is drawn once at the end instead: a pending proposal has to be
 * reachable after a reload whatever else is true.
 */
const Pending = React.createContext<{
  cards: ActionCard[];
  onDone: () => void;
  /** Each card is drawn by one turn: the first that names it, which is the turn that made it. */
  claim: (actionId: string, messageId: string) => boolean;
  /**
   * Messages the server wrote rather than the assistant: outcomes, refusals, a dead turn.
   *
   * Read from the stored transcript by id, because that is where they arrive. One block showed a
   * proposal "venter på bekreftelse" and "Endringen er utført" one line below it, both inside the
   * assistant's turn: two sentences true at different moments, reading as one contradiction.
   */
  systemLines: Set<string>;
}>({
  cards: [],
  onDone: () => {},
  claim: () => true,
  systemLines: new Set(),
});

/**
 * Action ids named anywhere in a message's parts.
 *
 * The whole part is walked rather than part.output: a stored tool result and the same result in
 * the live runtime do not carry the answer under the same key, and the first version of this
 * matched neither the turn as it streamed nor the turn after a reload. An id is an id wherever it
 * is written.
 */
function idsIn(parts: readonly unknown[]): string[] {
  const out: string[] = [];
  const walk = (value: unknown) => {
    if (!value) return;
    if (typeof value === 'string') {
      for (const m of value.matchAll(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g)) {
        out.push(m[0]);
      }
      return;
    }
    if (Array.isArray(value)) return value.forEach(walk);
    if (typeof value === 'object') return Object.values(value as Record<string, unknown>).forEach(walk);
  };
  parts.forEach(walk);
  return out;
}

/**
 * «Lagre forslaget som utkast» under the answer that offered it (offer_plan_save, owner 18.10). The click is
 * the manager's and runs the panel's own save; Hugin never saves.
 */
// which answers' offers were saved, kept outside the component: the chat draws itself anew once the
// proposal is saved and detached, and the button came back (owner 18.10)
const useSavedOffers = create<{ saved: Record<string, true>; mark: (id: string) => void }>((set) => ({
  saved: {},
  mark: (id) => set((s) => ({ saved: { ...s.saved, [id]: true } })),
}));
function SaveOffer() {
  const parts = useAuiState((s) => s.message.parts) as readonly { result?: unknown; output?: unknown }[];
  const messageId = useAuiState((s) => s.message.id) as string;
  const offer = React.useMemo(() => {
    let found: ReturnType<typeof saveOfferIn> = null;
    for (const p of parts || []) found = saveOfferIn(p?.result ?? p?.output) ?? found;
    return found;
  }, [parts]);
  const onSave = useAttached((x) => x.onSave);
  const saved = useSavedOffers((x) => !!x.saved[messageId]);
  const [saving, setSaving] = useState(false);
  if (!offer) return null;
  // saved: say so, no button; no proposal open any more: nothing to press
  if (!saved && !onSave) return null;
  return (
    <div className="pc-plan-save" data-testid="plan-save-offer">
      <span>
        {T('Uke {week}, {n} vakter', { week: offer.week, n: offer.shifts })}
        {offer.open ? `, ${T('{n} ledige', { n: offer.open })}` : ''}
      </span>
      {saved ? (
        <b>{T('Lagret som utkast')}</b>
      ) : (
        <button
          type="button"
          className="pc-send"
          disabled={saving}
          onClick={async () => {
            if (!onSave) return;
            setSaving(true);
            if (await onSave()) useSavedOffers.getState().mark(messageId);
            setSaving(false);
          }}
        >
          {saving ? T('Lagrer …') : T('Lagre forslaget som utkast')}
        </button>
      )}
    </div>
  );
}

function CardsForThisTurn() {
  const parts = useAuiState((s) => s.message.parts) as readonly unknown[];
  const messageId = useAuiState((s) => s.message.id) as string;
  const { cards, onDone, claim } = React.useContext(Pending);
  const elsewhere = useDock((x) => x.cards);
  const mine = React.useMemo(() => {
    const ids = new Set(idsIn(parts || []));
    return cards.filter((c) => ids.has(c.action_id) && claim(c.action_id, messageId));
  }, [parts, cards, claim, messageId]);
  if (!mine.length) return null;
  // the setup draws the cards in its own Forslag pane (#154 OP-06): the turn says one is there, once
  if (elsewhere)
    return (
      <div className="pc-plan-save" data-testid="hugin-proposal-pointer">
        <span>
          {mine.length === 1
            ? T('Forslaget er klart til gjennomgang.')
            : T('{n} forslag er klare til gjennomgang.', { n: mine.length })}
        </span>
        <button
          type="button"
          className="pc-header-button"
          onClick={() => window.dispatchEvent(new Event(SHOW_PROPOSALS))}
        >
          {T('Se forslaget')}
        </button>
      </div>
    );
  return (
    <div className="pc-cards pc-cards-inline">
      {mine.map((c) => (
        <Card key={c.action_id} card={c} onDone={onDone} />
      ))}
    </div>
  );
}

/**
 * Cards no turn in this conversation can draw.
 *
 * It reads the same runtime the turns read, from inside the provider, so both sides of the
 * decision see the same thing at the same moment. Deciding it from the stored transcript instead
 * made the card appear under the composer on the turn that created it and jump into place when the
 * transcript caught up: the runtime had the action_id all along, which is what the turn draws from.
 *
 * What is left here is a proposal whose turn is genuinely not in the conversation, from one that
 * has since been cleared. It stays reachable rather than becoming a card nobody can answer.
 */
/** What a manager reads when a turn fails. The real error is in the server log (finding 32). */
const COULD_NOT_ANSWER = () => T('AI-assistenten kunne ikke svare nå. Ingenting ble endret.');
// Bodies the chat route writes for the manager, already in Norwegian. For anything else, a status
// the transport turned into a JSON string or a provider's English, the fixed line is shown instead.
const FOR_THE_MANAGER = new Set(['spend_limit', 'message_too_large', 'bad_request', 'duplicate_message']);

function TurnError() {
  // Where ErrorPrimitive.Message reads it: the status of a turn that ended in an error.
  const error = useAuiState((s) => {
    const st = s.message.status as { type?: string; reason?: string; error?: unknown } | undefined;
    return st?.type === 'incomplete' && st.reason === 'error' ? st.error : undefined;
  }) as { message?: string } | string | undefined;
  const raw = typeof error === 'string' ? error : error?.message;
  let text = COULD_NOT_ANSWER();
  try {
    const body = JSON.parse(raw ?? '');
    if (body && FOR_THE_MANAGER.has(body.error) && typeof body.message === 'string') text = body.message;
  } catch {
    // not a response body: a stream error or a network failure, and the fixed line is right
  }
  return <ErrorPrimitive.Message>{text}</ErrorPrimitive.Message>;
}

/**
 * What the assistant is doing right now, from the tool it last called, and for how long (owner,
 * 2026-09-29): a month plan took up to three minutes a message and Christian's first question 43
 * seconds, under a bare "Jobber…" that did not say whether anything was happening.
 */
const SEARCHING: Record<string, string> = {
  shifts: 'Ser på vaktplanen',
  people: 'Ser på de ansatte',
  absences: 'Ser på fraværet',
  availability: 'Ser på hvem som kan jobbe',
  shift_types: 'Ser på vakttypene',
};
const STEP: Record<string, string> = {
  propose_change: 'Lager forslaget',
  copy_period: 'Kopierer vaktplanen',
  fill_shifts: 'Finner hvem som kan jobbe',
  plan_week: 'Lager et planforslag',
  search_help: 'Leser i brukerveiledningen',
  read_requests: 'Ser på forespørslene',
  read_month_close: 'Ser på måneden før lønn',
  find_api_route: 'Henter tallene',
  api_read: 'Henter tallene',
};
export function stepText(parts: readonly unknown[] | undefined): string {
  const calls = (parts || []).filter(
    (p): p is { type: string; toolName: string; args?: { find?: string } } =>
      !!p && typeof p === 'object' && (p as { type?: string }).type === 'tool-call',
  );
  const last = calls[calls.length - 1];
  if (!last) return T('Tenker');
  if (last.toolName === 'search') return T(SEARCHING[last.args?.find || ''] || 'Leter i bedriftens data');
  return T(STEP[last.toolName] || 'Jobber');
}

function Working() {
  const messages = useAuiState((s) => s.thread.messages) as unknown as readonly {
    role?: string;
    parts?: readonly unknown[];
  }[];
  const last = messages?.length ? messages[messages.length - 1] : null;
  const text = stepText(last?.role === 'assistant' ? last.parts : undefined);
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const timer = setInterval(() => setSeconds(Math.floor((Date.now() - started) / 1000)), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <p className="pc-working" aria-live="polite" data-testid="pc-working">
      {`${text}…`}
      {seconds >= 5 ? <span className="pc-working-time">{` ${seconds} s`}</span> : null}
    </p>
  );
}

function UnclaimedCards() {
  const messages = useAuiState((s) => s.thread.messages) as unknown as readonly {
    parts?: readonly unknown[];
  }[];
  const { cards, onDone } = React.useContext(Pending);
  const elsewhere = useDock((x) => x.cards);
  const loose = React.useMemo(() => {
    const known = new Set<string>();
    for (const m of messages || [])
      for (const id of idsIn((m.parts || []) as readonly unknown[])) known.add(id);
    return cards.filter((c) => !known.has(c.action_id));
  }, [messages, cards]);
  if (!loose.length || elsewhere) return null;
  return (
    <div className="pc-cards pc-cards-orphan">
      {loose.map((c) => (
        <Card key={c.action_id} card={c} onDone={onDone} />
      ))}
    </div>
  );
}

/**
 * The setup's Forslag pane (#154 OP-06): every waiting proposal with its own Utfør and Avvis, drawn there
 * from inside the conversation, so each card has the conversation's state as it would in the turn.
 */
function SideCards() {
  const target = useDock((x) => (x.target ? x.cards : null));
  const { cards, onDone } = React.useContext(Pending);
  if (!target) return null;
  return createPortal(
    <div className="pc-cards pc-cards-side" data-testid="hugin-side-cards">
      {cards.map((c) => (
        <Card key={c.action_id} card={c} onDone={onDone} side />
      ))}
      {/* the values a save refused as changed since kept (#154 HUG-06); the Undo is beside the setup's receipt */}
      <KeptList />
    </div>,
    target,
  );
}

/**
 * The choices a question offers (a new demo's opening question, issue #101; the form path's "Hvem mener du
 * med «kari»?" before it), as buttons that send the words, so the answer never has to be typed or
 * understood. Only on the latest message: a choice further up belongs to a question already answered.
 * clearComposer is off: inside a message the composer is that message's edit composer, which is not
 * open, and clearing it threw after the send (PC1h).
 */
function FormChoices() {
  const parts = useAuiState((s) => s.message.parts) as readonly {
    type?: string;
    name?: string;
    data?: { choices?: string[] };
  }[];
  const isLast = useAuiState((s) => s.message.isLast) as boolean;
  const choices = (parts || []).find((p) => p && p.type === 'data' && p.name === 'form')?.data?.choices;
  if (!isLast || !choices || !choices.length) return null;
  return (
    <div className="pc-choices" data-testid="form-choices">
      {choices.map((c) => (
        <ThreadPrimitive.Suggestion key={c} prompt={c} send clearComposer={false} className="pc-choice">
          {c}
        </ThreadPrimitive.Suggestion>
      ))}
    </div>
  );
}

/**
 * A way into a screen the server's answer names (the demo chip «Hvem passer på den ledige vakten?»,
 * issue #131): one link under the line, to a path inside the app only.
 */
function FormLink() {
  const parts = useAuiState((s) => s.message.parts) as readonly {
    type?: string;
    name?: string;
    data?: { link?: { to?: string; label?: string } };
  }[];
  const inRouter = useInRouterContext();
  const link = (parts || []).find((p) => p && p.type === 'data' && p.name === 'form' && p.data?.link)?.data
    ?.link;
  // a path in this app, never another site
  if (!link?.to || !link.label || !/^\/[a-z]/.test(link.to)) return null;
  return (
    <div className="pc-choices" data-testid="form-link">
      {inRouter ? (
        <Link className="pc-choice" to={link.to}>
          {link.label}
        </Link>
      ) : (
        <a className="pc-choice" href={link.to}>
          {link.label}
        </a>
      )}
    </div>
  );
}

function Message() {
  const role = useAuiState((s) => s.message.role) as string;
  const attached = useAttached((x) => x.attached);
  return (
    <MessagePrimitive.Root className={role === 'user' ? 'pc-msg pc-msg-user' : 'pc-msg'}>
      <div className="pc-role">
        {/* The manager's own messages are a bubble on the right, so the shape says who spoke and
            the label is not needed. The assistant keeps its name, once, above its turn. */}
        <MessagePrimitive.If assistant>{`${ASSISTANT}, AI`}</MessagePrimitive.If>
        {/* with a planning proposal attached the prototype names both speakers (#163) */}
        {attached && <MessagePrimitive.If user>{T('Deg')}</MessagePrimitive.If>}
      </div>
      <div className="pc-body">
        {/* Only what the model wrote is read as markdown. A manager who types an asterisk meant
            an asterisk. */}
        <MessagePrimitive.If user>
          <MessagePrimitive.Parts />
        </MessagePrimitive.If>
        <MessagePrimitive.If assistant>
          <MessagePrimitive.Parts components={{ Text }} />
        </MessagePrimitive.If>
        <MessagePrimitive.Error>
          <ErrorPrimitive.Root className="pc-error">
            <TurnError />
          </ErrorPrimitive.Root>
        </MessagePrimitive.Error>
        <MessagePrimitive.If assistant>
          <FormChoices />
          <FormLink />
          <CardsForThisTurn />
          <ReportCards />
          <SaveOffer />
        </MessagePrimitive.If>
      </div>
      <MessagePrimitive.If assistant>
        <PlanReceipt />
      </MessagePrimitive.If>
    </MessagePrimitive.Root>
  );
}

/**
 * What needs the administrator today, when the conversation is empty (map #5). Worked out on the
 * server from the screens' own data, no model call; pressing an item asks the chat about it.
 */
function Briefing() {
  const [items, setItems] = useState<BriefingItem[] | null>(null);
  useEffect(() => {
    loadBriefing()
      .then(setItems)
      .catch(() => setItems([]));
  }, []);
  if (!items || !items.length) return null;
  return (
    <div className="pc-brief" data-testid="briefing">
      <b>
        {items.length === 1
          ? T('Én ting trenger deg i dag')
          : T('{n} ting trenger deg i dag', { n: items.length })}
      </b>
      <ul>
        {items.map((i) => (
          <li key={i.key}>
            <ThreadPrimitive.Suggestion prompt={i.ask} send className="pc-brief-item">
              {i.text}
            </ThreadPrimitive.Suggestion>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * The month's included questions (owner, 2026-09-27: 100 for a company, 30 for a demo), read with the
 * thread and again after every answer, so the manager sees the limit coming rather than meeting it.
 */
function Usage() {
  // the share of the month's amount (owner, 2026-09-29): what a question costs varies tenfold, so a
  // count of questions said little; money is the limit, shown as a share, never in dollars
  const q = useQuery({
    queryKey: ['poc-usage'],
    queryFn: () => api<{ percent: number | null }>('/poc/usage'),
  });
  const p = q.data?.percent;
  if (p == null) return null;
  return (
    <div className={p >= 80 ? 'pc-usage low' : 'pc-usage'} data-testid="pc-usage">
      <span className="pc-usage-bar" aria-hidden="true">
        <i style={{ width: `${Math.max(2, p)}%` }} />
      </span>
      <span>{T('Du har brukt {p} % av månedens Hugin', { p })}</span>
    </div>
  );
}

/** Something to press on an empty conversation, with the job that shows Hugin best first (issue #24). */
const STARTERS = ['Lag vaktplan for neste uke', 'Hvem jobber i dag?', 'Hva koster denne uka?'];
function Starters() {
  // with planning off (the week is «Uka», #118) there is no plan to make: the planning job is not offered
  const planning = useTenant().mode === 'rota';
  return (
    <div className="pc-brief pc-starters" data-testid="starters">
      <b>{T('Prøv for eksempel')}</b>
      <ul>
        {STARTERS.filter((q) => planning || q !== STARTERS[0])
          .map((key) => T(key))
          .map((q) => (
            <li key={q}>
              <ThreadPrimitive.Suggestion prompt={q} send className="pc-brief-item">
                {q}
              </ThreadPrimitive.Suggestion>
            </li>
          ))}
      </ul>
    </div>
  );
}

/**
 * The prototype's welcome (assistant.js chatBody) when the chat opens with a planning proposal attached:
 * what Hugin has in context, from the real proposal.
 */
function PlanWelcome({ a }: { a: Attached }) {
  const what = [T('uke {week}', { week: a.week }), a.company, a.basis].filter(Boolean);
  const listed =
    what.length > 1
      ? T('{list} og {last}', { list: what.slice(0, -1).join(', '), last: what[what.length - 1] ?? '' })
      : what[0];
  return (
    <div className="pc-plan-welcome" data-testid="plan-welcome">
      <HuginMark className="pc-avatar" />
      <div>
        <b>{T('Hva vil du få til denne uka?')}</b>
        <p>
          {T(
            'Hugin har {what} i kontekst. Du kan be om en plan, justere et forslag eller stille et spørsmål.',
            {
              what: listed ?? '',
            },
          )}
        </p>
      </div>
    </div>
  );
}

/** «Forslaget vi jobber med» (prototype aa-current-ref), pinned under the conversation. */
function PlanCard({ a }: { a: Attached }) {
  return (
    <div className="pc-plan-card" data-testid="plan-current">
      <HuginMark className="pc-avatar" />
      <div>
        <span className="pc-plan-kicker">{T('Forslaget vi jobber med')}</span>
        <b>{a.title}</b>
        <span className="pc-plan-line">
          {[a.line, T('versjon {v}', { v: a.version }), a.facts, T('ikke lagret')].filter(Boolean).join(', ')}
        </span>
      </div>
    </div>
  );
}

/** «Oppdatert, versjon N»: Hugin's edits made into a new version by the planning panel. */
// by the answer that made the version, so each one shows under that answer, not all at the bottom (owner 18.10)
const usePlanReceipts = create<{
  byMessage: Record<string, string>;
  add: (messageId: string, t: string) => void;
  clear: () => void;
}>((set) => ({
  byMessage: {},
  add: (messageId, text) => set((s) => ({ byMessage: { ...s.byMessage, [messageId]: text } })),
  clear: () => set({ byMessage: {} }),
}));
/** The «Oppdatert» line under the answer whose edits made a new version. */
function PlanReceipt() {
  const id = useAuiState((s) => s.message.id) as string;
  const text = usePlanReceipts((x) => x.byMessage[id]);
  if (!text) return null;
  return (
    <div className="pc-msg pc-plan-receipt" data-testid="plan-receipt">
      <div className="pc-role">{T('Oppdatert')}</div>
      <div className="pc-body pc-plan-receipt-body">
        <HuginMark className="pc-avatar" />
        <span>{text}</span>
      </div>
    </div>
  );
}

/** Hugin's edits in the turn that just ended, if a tool handed any back (adjust_plan_proposal). */
export function editsOfTurn(
  messages: readonly { role: string; content?: readonly unknown[] }[],
): unknown[] | null {
  const last = [...messages].reverse().find((m) => m.role === 'assistant');
  let found: unknown[] | null = null;
  for (const part of last?.content ?? []) {
    const p = part as { result?: unknown; output?: unknown };
    found = editsIn(p.result ?? p.output) ?? found;
  }
  return found;
}

/** The week plan in the turn that just ended, if plan_week handed one back. */
export function proposalOfTurn(
  messages: readonly { role: string; content?: readonly unknown[] }[],
): ProposalAsk | null {
  const last = [...messages].reverse().find((m) => m.role === 'assistant');
  let found: ProposalAsk | null = null;
  for (const part of last?.content ?? []) {
    const p = part as { result?: unknown; output?: unknown };
    found = proposalIn(p.result ?? p.output) ?? found;
  }
  return found;
}

function Thread() {
  const attached = useAttached((x) => x.attached);
  const docked = useDock((x) => !!x.target);
  const placeholder = useDock((x) => x.placeholder);
  // a company that keeps hours only has no plan to fly over (#288 review: no shift words there)
  const planning = useTenant().mode === 'rota';
  // an empty conversation opens at its top, the briefing's heading first; following the bottom is for a
  // conversation with messages, where the newest one is what to read (#272 A-06: it opened 76 px down)
  const talking = useAuiState((s) => s.thread.messages.length > 0) as boolean;
  // detached (the proposal closed or saved): its receipts go with it
  useEffect(() => {
    if (!attached) usePlanReceipts.getState().clear();
  }, [attached]);
  return (
    <ThreadPrimitive.Root className="pc-thread">
      <ThreadPrimitive.Viewport className="pc-viewport" autoScroll={talking}>
        <ThreadPrimitive.Empty>
          {attached ? (
            <PlanWelcome a={attached} />
          ) : (
            <>
              <Briefing />
              <p className="pc-empty">
                {planning
                  ? T(
                      'Hei, jeg er Hugin, AI-assistenten i Timefly. Som ravnen til Odin flyr jeg over vaktplanen og forteller deg hva som trenger deg. Du bestemmer: ingenting endres før du trykker Utfør.',
                    )
                  : T(
                      'Hei, jeg er Hugin, AI-assistenten i Timefly. Som ravnen til Odin flyr jeg over timene og forteller deg hva som trenger deg. Du bestemmer: ingenting endres før du trykker Utfør.',
                    )}
              </p>
              <Starters />
            </>
          )}
        </ThreadPrimitive.Empty>
        <ThreadPrimitive.Messages components={{ Message }} />
        <UnclaimedCards />
        <SideCards />
        <ThreadPrimitive.If running>
          <Working />
        </ThreadPrimitive.If>
      </ThreadPrimitive.Viewport>
      {attached && <PlanCard a={attached} />}
      <ComposerPrimitive.Root className="pc-composer">
        {/* on a phone the keyboard waits for a tap: opened at once it covered the card (owner, 2026-10-01) */}
        <ComposerPrimitive.Input
          className="pc-input"
          aria-label={T('Melding til Hugin')}
          placeholder={
            attached
              ? T('Skriv til Hugin om uke {week} …', { week: attached.week })
              : docked && placeholder
                ? placeholder
                : T('Skriv her')
          }
          autoFocus={!TOUCH}
          rows={1}
        />
        <ThreadPrimitive.If running={false}>
          <ComposerPrimitive.Send className="pc-send">{T('Send')}</ComposerPrimitive.Send>
        </ThreadPrimitive.If>
        <ThreadPrimitive.If running>
          <ComposerPrimitive.Cancel className="pc-send">{T('Stopp')}</ComposerPrimitive.Cancel>
        </ThreadPrimitive.If>
      </ComposerPrimitive.Root>
      <Usage />
    </ThreadPrimitive.Root>
  );
}

/**
 * One proposed change, exactly as the server describes it. The panel does not compute the
 * preview and cannot: it draws the stored proposal, so what is confirmed is what was shown.
 */
/**
 * How long an absence is and what it takes from vacation, kept apart. The card said "(0 dager)" for
 * five days of sick leave: 0 was the vacation charge, read as the length. The length is the limits
 * route's count by the type's own day basis; the charge is the deduction preview. Neither is counted
 * here.
 */
function lengthAndCharge(
  e: { length_days?: number; length_unit?: string; deductible: boolean },
  charge: string | null,
) {
  const unit = e.length_unit ?? 'dager';
  const length =
    e.length_days === undefined
      ? null
      : `${e.length_days} ${e.length_days === 1 ? unit.replace(/er$/, '') : unit}`;
  return [length, e.deductible ? (charge ?? T('trekkes fra feriedager')) : T('trekker ikke feriedager')]
    .filter(Boolean)
    .join(', ');
}
const feriedager = (n: number) => (n === 1 ? T('{n} feriedag', { n }) : T('{n} feriedager', { n }));

/** The type's limit warnings, where the manager decides. They warn; they never stop the confirm. */
function LimitWarnings({ e }: { e: { warnings?: string[]; limits_unknown?: boolean } }) {
  if (e.limits_unknown) return <div className="pc-warn">{T('Grensene for typen kunne ikke sjekkes.')}</div>;
  if (!e.warnings?.length) return null;
  return (
    <ul className="pc-warn">
      {e.warnings.map((w) => (
        <li key={w}>
          <strong>{T('Over grensen:')}</strong> {w}
        </li>
      ))}
    </ul>
  );
}

/**
 * What the server said about a card's confirm or cancel, by action id. Kept outside the card:
 * the thread reloads right after the answer and the card mounts anew, which wiped a refusal or an
 * unknown outcome before anybody could read it (web coverage PC9, PC10, 2026-09-22).
 */
const cardProblems = new Map<string, string>();

// A copy or template card longer than this shows one line per day, with "Vis alle" for the shifts: a demo's
// first copy card was 27 lines, and on a phone its button was four screens down (owner, 2026-10-01). Only
// those, which make drafts: approving hours, punches, deletes and request answers keep every line, since
// each line there is a decision the manager should see before pressing.
const LONG_CARD = 8;
function byDay<T extends { date?: string | null }>(entries: readonly T[]): [string, T[]][] {
  const days = new Map<string, T[]>();
  for (const e of entries) if (e.date) days.set(e.date, [...(days.get(e.date) ?? []), e]);
  return [...days.entries()];
}

function Card({ card, onDone, side = false }: { card: ActionCard; onDone: () => void; side?: boolean }) {
  const [showAll, setShowAll] = useState(false);
  // a setting card's values as a form (#154 HUG-05/06): open at once in the setup's Forslag pane, behind «Endre»
  // in the chat; Utfør saves what the form shows, through the card, so its guard still holds
  const edit = card.kind === 'api_call' && canEdit(card.edit) ? card.edit : null;
  const [editing, setEditing] = useState(side && !!edit);
  const [draft, setDraft] = useState<Record<string, unknown>>(() => (edit ? valuesOf(edit.body) : {}));
  const edited = !!edit && editing && JSON.stringify(draft) !== JSON.stringify(valuesOf(edit.body));
  // demo companies only for now (owner, 2026-10-01): a real company's long copy card shows every line as before
  const demo = Boolean(useTenant().tenant?.is_demo);
  const [busy, setBusy] = useState(false);
  // a second press before React has drawn the first one's disabled button sends nothing (#287 H08)
  const inFlight = useRef(false);
  // Not while Hugin answers: that answer is written from the card as it stood when the message was sent, and a
  // card applied under it read «kortet venter fortsatt» (22.10). The button comes back when the answer is done.
  const answering = useAuiState((s) => s.thread.isRunning) as boolean;
  const [problem, setProblemState] = useState<string | null>(() => cardProblems.get(card.action_id) ?? null);
  const setProblem = (p: string | null) => {
    if (p == null) cardProblems.delete(card.action_id);
    else cardProblems.set(card.action_id, p);
    setProblemState(p);
  };
  const qc = useQueryClient();
  /**
   * The page under the panel, after a confirmed change.
   *
   * Bekreft wrote the shift and the grid behind it went on showing the old one until somebody
   * reloaded by hand. It calls the same rule the app's own writes call (invalidateShifts,
   * invalidateAbsences in api/), so the chat refreshes the page the way the product already does
   * rather than by a second mechanism.
   * Invalidating only refetches queries: the panel stays open and the conversation is untouched.
   *
   * Scoped to what changed. Shifts cover Vaktplan, Dagen and the counts on Innboks; absence covers
   * Fravær and the balances on the employee pages. Cancelling writes nothing and refreshes nothing.
   */
  const refreshThePage = () =>
    card.kind === 'absence_create' ||
    card.kind === 'absence_decision' ||
    card.kind === 'absence_update' ||
    card.kind === 'absence_withdraw'
      ? invalidateAbsences(qc)
      : invalidateShifts(qc);
  const act =
    (fn: () => Promise<unknown>, writes = false) =>
    async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      setBusy(true);
      setProblem(null);
      try {
        const r = (await fn()) as { message?: string; applied?: boolean | null } | undefined;
        // 202 means the server could not tell whether the write landed. Saying nothing here
        // would read as success.
        if (r && r.applied !== true && r.message) setProblem(r.message);
        if (writes) await refreshThePage();
        onDone();
      } catch (e) {
        // A refusal is the server's answer, not a glitch: it reloads so the card reflects it.
        const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
        setProblem(body?.message || (e instanceof Error ? e.message : String(e)));
        // A write that failed may still have landed, and a write that was refused leaves the page
        // as it was: either way the cheapest honest thing is to re-read it.
        if (writes) await refreshThePage();
        onDone();
      } finally {
        inFlight.current = false;
        setBusy(false);
      }
    };
  return (
    <div className="pc-card" aria-busy={busy}>
      {card.kind === 'api_call' ? (
        <>
          {/* a change through the API: every word here is the server's, from the route and the fields */}
          <div className="pc-card-what">{card.title}</div>
          {edit && editing ? (
            <SettingForm values={draft} onChange={setDraft} />
          ) : (
            <ul className="pc-days" data-testid="api-call-lines">
              {card.lines.map((l) => (
                <li key={l.field}>
                  {l.field}:{' '}
                  {l.before !== undefined ? (
                    <>
                      {l.before} → <strong>{l.after}</strong>
                    </>
                  ) : (
                    <strong>{l.after}</strong>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="pc-card-note">
            {T('Ingenting er endret før du trykker {button}.', { button: card.button })}
          </div>
        </>
      ) : card.kind === 'shift_ops' ? (
        <>
          <div className="pc-card-what">{card.title}</div>
          {demo &&
          (card.op === 'copy' || card.op === 'template') &&
          card.entries.length > LONG_CARD &&
          !showAll ? (
            <>
              <ul className="pc-days" data-testid="card-days">
                {byDay(card.entries).map(([date, list]) => {
                  const open = list.filter((e) => e.employee === 'Ledig vakt').length;
                  return (
                    <li key={date}>
                      {`${dag(date)}: `}
                      <strong>{list.length === 1 ? T('1 vakt') : T('{n} vakter', { n: list.length })}</strong>
                      {open
                        ? `, ${open === 1 ? T('{n} ledig', { n: open }) : T('{n} ledige', { n: open })}`
                        : ''}
                    </li>
                  );
                })}
              </ul>
              <button type="button" className="pc-more" onClick={() => setShowAll(true)}>
                {T('Vis alle {n} vakter', { n: card.entries.length })}
              </button>
            </>
          ) : (
            <ul className="pc-days">
              {card.entries.map((e, i) => (
                <li key={`${e.employee}-${e.date}-${e.start}-${i}`}>
                  {e.decision ? (
                    <strong>{e.decision === 'approve' ? T('Godkjenn') : T('Avslå')}: </strong>
                  ) : null}
                  {e.employee ? `${e.employee}, ` : ''}
                  {e.date ? `${dag(e.date)} ` : ''}
                  {e.start ? (
                    <strong>
                      {e.start}–{e.end}
                    </strong>
                  ) : null}
                  {e.detail ? `, ${e.detail}` : ''}
                </li>
              ))}
            </ul>
          )}
          <div className="pc-card-note">{card.note}</div>
        </>
      ) : card.kind === 'absence_create' ? (
        <>
          <div className="pc-card-what">{T('Registrer fravær')}</div>
          <ul className="pc-days">
            {card.entries.map((e, i) => (
              <li key={`${e.employee}-${e.from}-${i}`}>
                {e.employee}
                {e.type ? `, ${e.type}` : ''}, {dag(e.from)}
                {e.to !== e.from ? ` ${T('til {day}', { day: dag(e.to) })}` : ''}
                <div className="pc-card-note">
                  {lengthAndCharge(
                    e,
                    e.days === undefined ? null : T('trekker {days}', { days: feriedager(e.days) }),
                  )}
                </div>
                <LimitWarnings e={e} />
              </li>
            ))}
          </ul>
          <div className="pc-card-note">
            {/* POST /admin/absence-requests stores it as approved and takes the days at once; this
                said they went "when the absence is approved", which never happens afterwards. */}
            {T('Registreres som godkjent fravær.')}
            {/* Only where something draws on vacation: under "trekker ikke feriedager" it read as a
                contradiction. */}
            {card.entries.some((e) => e.deductible) ? ` ${T('Feriedagene trekkes med en gang.')}` : ''}
          </div>
        </>
      ) : card.kind === 'absence_update' || card.kind === 'absence_withdraw' ? (
        <>
          <div className="pc-card-what">
            {card.kind === 'absence_update' ? T('Endre fravær') : T('Trekk tilbake fravær')}
          </div>
          <ul className="pc-days">
            {card.entries.map((e) => (
              <li key={`${e.employee}-${e.from}`}>
                {e.employee}
                {card.kind === 'absence_update' ? (
                  <>
                    {e.type_before ? `, ${e.type_before}` : ''}, {dag(e.from_before ?? e.from)}
                    {e.to_before && e.to_before !== e.from_before
                      ? ` ${T('til {day}', { day: dag(e.to_before) })}`
                      : ''}
                    {' → '}
                    <strong>
                      {e.type && e.type !== e.type_before ? `${e.type}, ` : ''}
                      {dag(e.from)}
                      {e.to !== e.from ? ` ${T('til {day}', { day: dag(e.to) })}` : ''}
                    </strong>
                  </>
                ) : (
                  <>
                    {e.type ? `, ${e.type}` : ''}, {dag(e.from)}
                    {e.to !== e.from ? ` ${T('til {day}', { day: dag(e.to) })}` : ''}
                  </>
                )}
              </li>
            ))}
          </ul>
          <div className="pc-card-note">
            {/* The numbers are the API's own, the length from the limits route and the charge
                from the deduction preview, never counted here. */}
            {card.entries.map((e) =>
              card.kind === 'absence_update'
                ? `${lengthAndCharge(
                    e,
                    e.days_before === undefined || e.days_after === undefined
                      ? null
                      : e.days_before === e.days_after
                        ? T('trekker {days}, som før', { days: feriedager(e.days_after) })
                        : T('trekker {days}, var {before}', {
                            days: feriedager(e.days_after),
                            before: e.days_before,
                          }),
                  )}.`
                : e.deductible
                  ? T('{days} legges tilbake på feriesaldoen.', { days: feriedager(e.days_back ?? 0) })
                  : T('Typen trekker ikke feriedager, så saldoen endres ikke.'),
            )}{' '}
            {T('Ingenting er endret før du bekrefter.')}
          </div>
          {card.entries.map((e) => (
            <LimitWarnings key={`w-${e.employee}-${e.from}`} e={e} />
          ))}
        </>
      ) : card.kind === 'absence_decision' ? (
        <>
          <div className="pc-card-what">{T('Avgjør fravær')}</div>
          <ul className="pc-days">
            {card.entries.map((e, i) => (
              <li key={`${e.employee}-${e.from}-${i}`}>
                <strong>{e.decision === 'approve' ? T('Godkjenn') : T('Avslå')}: </strong>
                {e.employee}
                {e.type ? `, ${e.type}` : ''}, {dag(e.from)}
                {e.to !== e.from ? ` ${T('til {day}', { day: dag(e.to) })}` : ''}
              </li>
            ))}
          </ul>
          <div className="pc-card-note">
            {T(
              'Godkjenning trekker feriedager der fraværstypen gjør det. Ingenting er avgjort før du bekrefter.',
            )}
          </div>
        </>
      ) : card.kind !== 'move' ? (
        <>
          <div className="pc-card-what">
            {/* A set can cover more than one person, so each line says who it is for. */}
            {[...new Set(card.entries.map((e) => e.employee).filter(Boolean))].join(', ') || T('Nye vakter')}
          </div>
          <ul className="pc-days">
            {card.entries.map((e) => (
              <li key={`${e.employee}-${e.date}-${e.start}`}>
                {e.employee ? `${e.employee}, ` : ''}
                {dag(e.date)}{' '}
                <strong>
                  {e.start}–{e.end}
                </strong>
                {/* A night shift is the one case where the day it starts and the day it ends
                    differ, so both are shown rather than a suffix the eye slides over. */}
                {e.crosses_midnight ? ` (${T('natt til {day}', { day: dag(nextDay(e.date)) })})` : ''}
              </li>
            ))}
          </ul>
          <div className="pc-card-note">
            {card.kind === 'publish'
              ? T(
                  '{n, plural, one {# vakt} other {# vakter}} blir synlige for ansatte. Varsling følger bedriftens vanlige innstillinger. Utsendte varsler kan ikke trekkes tilbake.',
                  { n: card.entries.length },
                )
              : card.entries.length === 1
                ? T('Én ny vakt, som utkast.')
                : T('{n} nye vakter, som utkast.', { n: card.entries.length })}
          </div>
        </>
      ) : card.moves_day ? (
        <>
          {/* When the day moves, the two dates are the whole point, so each is labelled and on
              its own line. Putting them side by side read as one long date followed by another. */}
          <div className="pc-card-what">{card.employee ?? T('Vakt {id}', { id: card.shift_id })}</div>
          <div className="pc-card-move">
            <span className="pc-leg">
              <span className="pc-leg-label">{T('Fra')}</span>
              <span>
                {card.from.date ? `${dag(card.from.date)} ` : ''}
                {card.from.start}–{card.from.end}
              </span>
            </span>
            <span className="pc-arrow" aria-hidden="true">
              ↓
            </span>
            <span className="pc-leg">
              <span className="pc-leg-label">{T('Til')}</span>
              <strong>
                {dag(card.to.date)} {card.to.start}–{card.to.end}
              </strong>
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="pc-card-what">
            {card.employee ? `${card.employee}, ` : ''}
            {dag(card.date)}
          </div>
          <div className="pc-card-change">
            {card.from.start}–{card.from.end} →{' '}
            <strong>
              {card.to.start}–{card.to.end}
            </strong>
          </div>
        </>
      )}
      {/* announced, so a refusal or an unknown outcome is heard as well as seen (#287 H08, H11) */}
      <output className={problem ? 'pc-error' : 'pc-sr'}>{problem ?? (busy ? T('Sender …') : '')}</output>
      <div className="pc-card-buttons">
        <button
          type="button"
          className="pc-send"
          disabled={busy || answering}
          title={answering ? 'Venter til Hugin har svart' : undefined}
          onClick={act(async () => {
            // an edited setting card is saved as edited first; the confirm then writes it with its guard
            if (edited && card.kind === 'api_call') await editAction(card.action_id, draft);
            // what was saved is what the form showed: the Undo expects it, a refusal keeps it
            const shown =
              card.kind === 'api_call'
                ? {
                    ...card,
                    edit:
                      edit && editing ? { ...edit, body: { ...edit.body, ...draft } } : (edit ?? undefined),
                  }
                : null;
            try {
              const r = await confirmAction(card.action_id);
              if (shown) noteOutcome(shown, r);
              return r;
            } catch (e) {
              if (shown) noteOutcome(shown, undefined, e);
              throw e;
            }
          }, true)}
        >
          {card.kind === 'shift_ops' || card.kind === 'api_call'
            ? card.button
            : card.kind === 'publish'
              ? T('Publiser')
              : card.kind === 'absence_decision'
                ? T('Utfør')
                : card.kind === 'absence_withdraw'
                  ? T('Trekk tilbake')
                  : T('Bekreft')}
        </button>
        {edit && !editing && (
          <button type="button" className="pc-ghost" disabled={busy} onClick={() => setEditing(true)}>
            {T('Endre')}
          </button>
        )}
        <button
          type="button"
          className="pc-ghost"
          disabled={busy}
          onClick={act(() => cancelAction(card.action_id))}
        >
          {T('Avbryt')}
        </button>
      </div>
    </div>
  );
}

/**
 * The thread, seeded from the server.
 *
 * It is remounted whenever the stored transcript changes underneath it, which is what happens
 * when a confirmed change writes its outcome into the history: assistant-ui takes its starting
 * messages once, so a new starting point is a new mount. The alternative, pushing a message
 * into a live runtime, would put the browser's copy and the server's copy into disagreement,
 * and the server's is the one that has to win after a reload.
 */
function Conversation({ seed, onTurn }: { seed: StoredMessage[]; onTurn: () => void }) {
  const qc = useQueryClient();
  // the stored history's last turn is already handled: a remount must not open its plan or edits again
  const handled = useRef<string | null>(seed[seed.length - 1]?.id ?? null);
  const runtime = useChatRuntime({
    messages: seed as never,
    transport: new AssistantChatTransport({
      api: `${API_BASE}/poc/chat`,
      // Read at request time, so a company switch or a fresh login carries straight through.
      headers: () => {
        const token = getToken();
        return token ? { Authorization: `Bearer ${token}` } : {};
      },
      /**
       * Send the new message, not the conversation.
       *
       * The server already holds the authoritative transcript; posting the whole thing back on
       * every turn duplicated it and grew without limit, until a long conversation exceeded the
       * request body limit and the turn failed before it reached the model. Raising the limit
       * moves that wall rather than removing it. The last message is what is new; the rest is
       * read from the database on the server side.
       */
      prepareSendMessagesRequest: ({ messages, id, trigger, messageId }) => {
        // a planning proposal attached from Vaktplan rides with every message while it is attached (#163)
        const planning = useAttached.getState().attached?.context;
        // the saved week on Vaktplan, so Hugin knows whether it is drafts or published (owner 18.10)
        const week = useDock.getState().week;
        // the setup's Hugin pane (#154): the server reads the setup's facts itself
        const setup = useDock.getState().setup;
        return {
          body: {
            message: messages[messages.length - 1],
            id,
            trigger,
            messageId,
            ...(planning ? { planning } : {}),
            ...(week ? { week } : {}),
            ...(setup ? { setup: true } : {}),
          },
        };
      },
    }),
  });
  /**
   * The cards are asked for when the turn is over, not while it runs.
   *
   * A tool result resolves before the step it is in, and the text of that step is held until the
   * step finishes, so asking mid-turn drew the card first and let its sentence arrive above it a
   * moment later. They belong together, in reading order. Waiting for the turn to end costs the
   * card the time the sentence was being written anyway, which is the beat it should have waited.
   */
  // the question a cold email's demo asks on arrival, once (askOnOpen); a moment after mount, so a
  // mount that is thrown away at once (React's double mount in development) does not use it up
  // biome-ignore lint/correctness/useExhaustiveDependencies: once per mount of this conversation
  useEffect(() => {
    if (!askWaiting()) return;
    const id = setTimeout(() => {
      const text = takeAsk();
      if (text) runtime.thread.append({ role: 'user', content: [{ type: 'text', text }] });
    }, 300);
    return () => clearTimeout(id);
  }, []);
  // «Prøv denne føringen» in Vaktplan's comparison: send the instruction at once, with the proposal attached
  useEffect(() => {
    const send = () => {
      const text = takeSend();
      if (text) runtime.thread.append({ role: 'user', content: [{ type: 'text', text }] });
    };
    // sent before this conversation was on screen (the docked tab mounts after the event)
    send();
    window.addEventListener('timefly:ai-send', send);
    return () => window.removeEventListener('timefly:ai-send', send);
  }, [runtime]);
  // a draft from draftForHugin: on mount (the panel was closed) or at once (it was open)
  useEffect(() => {
    const put = () => {
      if (draft == null) return;
      runtime.thread.composer.setText(draft);
      draft = null;
    };
    put();
    window.addEventListener('timefly:ai-draft', put);
    return () => window.removeEventListener('timefly:ai-draft', put);
  }, [runtime]);
  useEffect(
    () =>
      runtime.thread.subscribe(() => {
        if (!runtime.thread.getState().isRunning) {
          // Hugin changed the attached proposal: the panel makes the next version, the chat says which
          const { attached, onEdits } = useAttached.getState();
          const turn = runtime.thread.getState().messages as unknown as {
            id: string;
            role: string;
            content?: unknown[];
          }[];
          const lastId = turn[turn.length - 1]?.id;
          const fresh = !!lastId && lastId !== handled.current;
          const edits = attached && onEdits && fresh ? editsOfTurn(turn) : null;
          // Hugin planned a week (plan_week): Vaktplan opens Planlegg on it with the three drawn
          const asked = fresh ? proposalOfTurn(turn) : null;
          if (lastId) handled.current = lastId;
          if (asked) useProposalAsk.getState().ask(asked);
          if (edits && onEdits && lastId)
            void onEdits(edits).then((v) =>
              usePlanReceipts
                .getState()
                .add(
                  lastId,
                  v
                    ? T('Forslaget er oppdatert til versjon {v}. Se det i planen før du lagrer.', { v })
                    : T('Endringen kunne ikke brukes. Forslaget er som før.'),
                ),
            );
          onTurn();
          qc.invalidateQueries({ queryKey: ['poc-usage'] });
        }
      }),
    [runtime, onTurn, qc],
  );
  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <Thread />
    </AssistantRuntimeProvider>
  );
}

/** The calendar day after a YYYY-MM-DD, for naming the morning a night shift ends on. */
function nextDay(date: string): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

/**
 * "Rapporter et problem" (map #5, owner 2026-09-24): every line that would be sent, each with a box
 * to take it out, the cards and what became of them, and a line for what went wrong. What is sent
 * is exactly what is ticked; the server rebuilds it from the stored conversation.
 */
function Report({ onClose }: { onClose: () => void }) {
  const [data, setData] = useState<{ items: ReportItem[]; cards: { id: string; text: string }[] } | null>(
    null,
  );
  const [out, setOut] = useState<Set<string>>(new Set());
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    loadReport()
      .then(setData)
      .catch(() => setData({ items: [], cards: [] }));
  }, []);
  const send = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const r = await sendReport(
        text.trim(),
        (data?.items ?? []).map((m) => m.id).filter((id) => !out.has(id)),
        (data?.cards ?? []).map((c) => c.id).filter((id) => !out.has(id)),
      );
      setDone(r.message);
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
      setProblem(body?.message || T('Rapporten kunne ikke sendes. Prøv igjen.'));
    } finally {
      setBusy(false);
    }
  };
  if (done)
    return (
      <div className="pc-thread pc-report" data-testid="report">
        <p className="pc-system">{done}</p>
        <button type="button" className="pc-send" onClick={onClose}>
          {T('Tilbake til samtalen')}
        </button>
      </div>
    );
  return (
    <div className="pc-thread pc-report" data-testid="report">
      <p className="pc-report-lead">{T('Dette sendes til Timefly. Ta bort linjer du ikke vil dele.')}</p>
      <label className="pc-report-label" htmlFor="pc-report-text">
        {T('Hva virket ikke?')}
      </label>
      <textarea
        id="pc-report-text"
        className="pc-input"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {data === null ? (
        <p className="pc-working">{T('Henter samtalen…')}</p>
      ) : (
        <>
          <ul className="pc-report-items">
            {data.items.map((m) => (
              <li key={m.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={!out.has(m.id)}
                    onChange={() => {
                      const next = new Set(out);
                      if (next.has(m.id)) next.delete(m.id);
                      else next.add(m.id);
                      setOut(next);
                    }}
                  />
                  <span>
                    <b>{m.who}:</b> {m.text}
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {data.cards.length > 0 && (
            <>
              <p className="pc-report-label">{T('Forslag og utfall i dag')}</p>
              <ul className="pc-report-items">
                {data.cards.map((c) => (
                  <li key={c.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={!out.has(c.id)}
                        onChange={() => {
                          const next = new Set(out);
                          if (next.has(c.id)) next.delete(c.id);
                          else next.add(c.id);
                          setOut(next);
                        }}
                      />
                      <span>{c.text}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
      {problem && <p className="pc-error">{problem}</p>}
      <div className="pc-card-buttons">
        <button type="button" className="pc-send" disabled={busy || text.trim().length < 3} onClick={send}>
          {T('Send rapport')}
        </button>
        <button type="button" className="pc-ghost" disabled={busy} onClick={onClose}>
          {T('Avbryt')}
        </button>
      </div>
    </div>
  );
}

export function PocChatPanel() {
  const [open, setOpen] = useState(false);
  const returnFocus = useRef<HTMLElement | null>(null);
  // on a phone the panel covers the app, so a new demo's question is pointed at, not opened (owner, 2026-10-01)
  const [nudge, setNudge] = useState(false);
  // a dot on the tab until Hugin is opened (owner, 2026-10-01)
  const [unseen, setUnseen] = useState(false);
  // focus goes back to what opened Hugin (the top bar's button, Saker's link), else to the top bar's button,
  // whether it was on the body or still inside the closed panel (#272 T-01, A-07); the chat opens at its top,
  // not scrolled past its first card by the field's focus (A-06)
  const wasOpen = useRef(open);
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!wasOpen.current && open) {
      opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      requestAnimationFrame(() => {
        const vp = document.querySelector<HTMLElement>('.pc-viewport');
        if (vp && vp.scrollHeight - vp.clientHeight < 400) vp.scrollTop = 0;
      });
    }
    if (wasOpen.current && !open) {
      const a = document.activeElement;
      const lost = !a || a === document.body || !!a.closest('.pc-panel');
      if (lost) {
        const back =
          opener.current?.isConnected && !opener.current.closest('.pc-panel') ? opener.current : null;
        (back ?? document.querySelector<HTMLButtonElement>('button.tophugin'))?.focus();
      }
    }
    wasOpen.current = open;
  }, [open]);
  // the top bar's Hugin button shows the same dot and pulse the corner tab did (#272: the tab covered content)
  useEffect(() => {
    const cue = unseen && !open ? 'unseen' : nudge && !open ? 'nudge' : '';
    if (cue) document.documentElement.dataset.aiCue = cue;
    else delete document.documentElement.dataset.aiCue;
  }, [unseen, nudge, open]);
  // Esc minimises the open panel, as it closes other popovers
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      if (returnFocus.current?.isConnected) returnFocus.current.focus();
      returnFocus.current = null;
    };
  }, [open]);
  const [reporting, setReporting] = useState(false);
  const [pending, setPending] = useState<ActionCard[]>([]);
  const [seed, setSeed] = useState<StoredMessage[] | null>(null);
  const [generation, setGeneration] = useState(0);
  // Vaktplan on a phone opens the panel from its summary bar (plan 0029 decision 14), and shows
  // that button only while the panel exists, which the attribute says
  useEffect(() => {
    const show = () => {
      if (document.activeElement instanceof HTMLElement) returnFocus.current = document.activeElement;
      setNudge(false);
      setUnseen(false);
      // on Vaktplan (desktop) the conversation opens in the side panel's Hugin tab, not over the grid
      if (useDock.getState().dockable) {
        setOpen(false);
        window.dispatchEvent(new Event('timefly:hugin-dock'));
        return;
      }
      setOpen(true);
    };
    document.documentElement.dataset.ai = 'on';
    window.addEventListener('timefly:ai-open', show);
    return () => {
      delete document.documentElement.dataset.ai;
      window.removeEventListener('timefly:ai-open', show);
    };
  }, []);
  // Which turn drew which card, so the same proposal cannot appear twice when more than one
  // message mentions its id. Cleared whenever the pending set changes.
  const claimed = useRef(new Map<string, string>());
  // The running flag is no longer needed to decide where a card goes: the runtime decides that, and
  // it is right from the first render of the turn. It is kept out of the signature entirely so
  // nothing re-reads the transcript mid-stream again.
  const refresh = useCallback(() => {
    loadPending()
      .then((l) => {
        setPending(l);
        // the Vaktplan draws the same proposals on its grid (V19 slice 5)
        window.dispatchEvent(new Event('timefly:ai-pending'));
      })
      .catch(() => setPending([]));
  }, []);
  // a proposal used or dropped on the Vaktplan: read the list again
  useEffect(() => {
    window.addEventListener('timefly:ai-refresh', refresh);
    return () => window.removeEventListener('timefly:ai-refresh', refresh);
  }, [refresh]);
  /**
   * Re-read the transcript and remount the thread on it, after a decision has written a line
   * into it. Only ever called when the thread is idle: remounting a running thread would throw
   * away the answer being streamed into it, and the server's copy would not have it yet.
   */
  const reseed = useCallback(() => {
    loadThread()
      .then((m) => {
        setSeed(m);
        setGeneration((g) => g + 1);
      })
      .catch(() => setSeed([]));
  }, []);
  // A card whose turn is still in the transcript is drawn by that turn. The rest, from a
  // conversation that has since been cleared, are drawn at the end so they stay reachable.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pending and generation are the triggers, not inputs; a new pending list or a remount must forget which turn claimed which card
  useEffect(() => {
    claimed.current = new Map();
  }, [pending, generation]);

  // What the server wrote, by its text, from the copy it holds.
  const systemLines = React.useMemo(
    () =>
      new Set(
        (seed || [])
          // a greeting (a new demo's opening line) is Hugin talking, not an outcome with a ✓
          .filter(
            (m) =>
              m.metadata &&
              m.metadata.source === 'system' &&
              (m.metadata as { kind?: string }).kind !== 'greeting',
          )
          .map((m) =>
            (m.parts || [])
              .filter((p) => p.type === 'text' && p.text)
              .map((p) => String(p.text).trim())
              .join('\n'),
          )
          .filter(Boolean),
      ),
    [seed],
  );

  // On mount, so a reload shows what the server holds rather than an empty panel. A conversation that
  // holds only the server's opening line (a new demo's question, issue #101; its first card from #24
  // until 2026-10-13) opens the panel once, so nobody has to find Hugin: five of five demo visitors
  // had asked it nothing (2026-09-30)
  useEffect(() => {
    refresh();
    loadThread()
      .then((m) => {
        setSeed(m);
        // a cold email's demo: open at once, on a phone too, and Hugin asks its question (askOnOpen)
        if (askWaiting()) {
          setHuginOpening('showing');
          return setOpen(true);
        }
        const first = m.length === 1 && m[0]?.metadata?.source === 'system' ? m[0] : null;
        if (!first) return setHuginOpening('done');
        const key = `timefly.hugin.greeted.${first.id}`;
        try {
          // a second load (React's dev double effect, a remount) finds the key it just set: the opening
          // still on screen stays 'showing', or the welcome would open over it (DT1, 20.10)
          if (localStorage.getItem(key)) {
            if (useHuginOpening.getState().state !== 'showing') setHuginOpening('done');
            return;
          }
          localStorage.setItem(key, '1');
        } catch {
          // no storage: open it anyway; the worst case is opening again on the next load
        }
        // the welcome overview waits until this is put away (#130)
        setHuginOpening('showing');
        if (window.matchMedia?.('(max-width: 900px)').matches) {
          setNudge(true);
          setUnseen(true);
        } else setOpen(true);
      })
      .catch(() => {
        setSeed([]);
        setHuginOpening('done');
      });
  }, [refresh]);
  // the opening put away (panel closed, bubble gone): the welcome overview may show (#130)
  useEffect(() => {
    if (!open && !nudge && useHuginOpening.getState().state === 'showing') setHuginOpening('done');
  }, [open, nudge]);

  const dockTarget = useDock((x) => x.target);
  // the count whoever draws the cards elsewhere shows (the setup's «Forslag (n)»)
  useEffect(() => {
    useDock.getState().set({ pending: pending.length });
  }, [pending.length]);
  // Moving between floating and docked mounts the conversation anew; it is read from the server
  // then, so what was said on another page a moment ago is there (one conversation, owner 18.10)
  const docked = Boolean(dockTarget);
  const firstDock = useRef(true);
  // biome-ignore lint/correctness/useExhaustiveDependencies: a change of place is the trigger
  useEffect(() => {
    if (firstDock.current) {
      firstDock.current = false;
      return;
    }
    reseed();
  }, [docked]);
  const dockLine = useDock((x) => x.line);
  const attachedNow = useAttached((x) => x.attached);
  const stack = (
    <div className={dockTarget ? 'pc-stack pc-docked' : 'pc-stack'} data-testid="hugin-stack">
      <div className="pc-header">
        {/* docked, the panel's tab already says Hugin: one slim row with the week and the two actions
            (owner 18.10: four header rows before the conversation) */}
        {dockTarget ? (
          <span className="pc-dockline" data-testid="hugin-context">
            {attachedNow ? attachedNow.line : dockLine}
          </span>
        ) : (
          <span className="pc-name">
            <HuginMark className="pc-avatar" />
            {ASSISTANT}
            <span className="pc-ai" title={T('Hugin er en AI-assistent')}>
              AI
            </span>
          </span>
        )}
        <div className="pc-header-actions">
          <button
            type="button"
            className="pc-header-button"
            onClick={() => clearThread().then(reseed)}
            title={T('Tøm samtalen og begynn på nytt')}
          >
            {T('Ny samtale')}
          </button>
          <button
            type="button"
            className="pc-header-button"
            onClick={() => setReporting(true)}
            aria-label={T('Rapporter et problem')}
            title={T('Rapporter et problem: send samtalen til Timefly hvis noe ikke virket')}
          >
            {T('Rapporter et problem')}
          </button>
          {/* minimise, not close: the conversation and its cards stay, "Spør Hugin" opens it again
                    (owner, 2026-09-27: the × had been pushed out of the header and could not be found) */}
          {!dockTarget && (
            <button
              type="button"
              className="pc-header-button pc-close"
              onClick={() => setOpen(false)}
              aria-label={T('Minimer')}
              title={T('Minimer (samtalen blir liggende, Esc)')}
            >
              –
            </button>
          )}
        </div>
      </div>
      {reporting ? (
        <Report onClose={() => setReporting(false)} />
      ) : seed === null ? (
        <div className="pc-thread pc-loading">{T('Henter samtalen…')}</div>
      ) : (
        <Pending.Provider
          value={{
            cards: pending,
            onDone: () => {
              refresh();
              // A decision can write a line into the transcript, so it is re-read.
              reseed();
            },
            systemLines,
            claim: (actionId, messageId) => {
              const held = claimed.current.get(actionId);
              if (held === undefined) claimed.current.set(actionId, messageId);
              return (claimed.current.get(actionId) ?? messageId) === messageId;
            },
          }}
        >
          <Conversation key={generation} seed={seed} onTurn={refresh} />
        </Pending.Provider>
      )}
    </div>
  );
  return (
    <>
      {/* open over the page, Tab stays in the conversation until Esc, as in the side panels (#272 A-R09) */}
      <div
        className={open ? 'pc-panel pc-open' : 'pc-panel'}
        onKeyDown={(e) => open && !dockTarget && trapTab(e, e.currentTarget)}
      >
        {nudge && !open && (
          <div className="pc-nudge" data-testid="hugin-nudge">
            <button
              type="button"
              className="pc-nudge-open"
              onClick={() => {
                setNudge(false);
                setUnseen(false);
                setOpen(true);
              }}
            >
              <HuginMark className="pc-avatar" />
              <span>
                <b>{T('Jeg har et spørsmål til deg')}</b>
                <span className="pc-nudge-cta">{T('Trykk for å svare →')}</span>
              </span>
            </button>
            <button
              type="button"
              className="pc-nudge-x"
              aria-label={T('Lukk')}
              onClick={() => setNudge(false)}
            >
              ✕
            </button>
          </div>
        )}
        <button
          type="button"
          className={nudge && !open ? 'pc-tab pc-tab-nudge' : 'pc-tab'}
          onClick={(e) => {
            returnFocus.current = e.currentTarget;
            setNudge(false);
            setUnseen(false);
            if (useDock.getState().dockable) window.dispatchEvent(new Event('timefly:hugin-dock'));
            else setOpen((v) => !v);
          }}
          aria-expanded={open}
        >
          {unseen && !open && (
            <span className="pc-dot" aria-label={T('1 ny melding')}>
              1
            </span>
          )}
          {open ? (
            T('Lukk')
          ) : (
            <>
              <HuginMark className="pc-avatar" />
              {/* the words hide on a phone, where the round button sits over content (#272 L-31) */}
              <span className="pc-tl">{T('Spør {name}', { name: ASSISTANT })}</span>
            </>
          )}
        </button>
        {open && !dockTarget && stack}
      </div>
      {/* one conversation: on Vaktplan the same stack is drawn in the side panel's Hugin tab */}
      {dockTarget && createPortal(stack, dockTarget)}
    </>
  );
}
